import { useState } from "react";
import { ImageOff } from "lucide-react";
import { Img } from "@/components/shared/Img";
import { imgUrl } from "@/lib/api";
import { resolveDisplay } from "@/lib/display";
import { cn } from "@/lib/utils";

/**
 * The framed picture of an event – card, listing row and detail page all use it, so one setting looks the same
 * everywhere. `display` comes from lib/display.js (resolveDisplay / displayFor).
 *
 * - whole picture (fit contain): the frame takes the ratio asked for; when that differs from the picture, a soft blurred
 *   copy fills the bands instead of flat colour. Nothing is ever cropped.
 * - fill (fit cover): object-position follows the focus point.
 * - a skeleton while loading, a quiet placeholder if the file cannot be loaded, no layout shift for the house's own
 *   pictures (their size is known up front). A picture whose size is unknown (external URL) fills the standard frame
 *   until it has loaded, then is re-resolved from its real size (`measure`) and the parent is told via `onResolve`.
 * `children` are overlays (badges, buttons) – the callers only pass them when the picture fills the frame.
 */
export const EventImage = ({ src, display, alt = "", sizes, width = 1200, widths, priority = false, hover = false, measure = true, onResolve, className, imgClassName, onLoad, testId, children }) => {
  const [state, setState] = useState("loading");
  const [measured, setMeasured] = useState(null);
  const d = measured && display.natural == null ? resolveDisplay(display.settings, measured, { minRatio: display.minRatio }) : display;
  const { fit, ratio, focus, bands } = d;
  const whole = fit === "contain";
  const loaded = (e) => {
    setState("loaded");
    const el = e.currentTarget;
    if (measure && display.natural == null && el.naturalWidth && el.naturalHeight) {
      const m = { w: el.naturalWidth, h: el.naturalHeight };
      setMeasured(m);
      onResolve?.(resolveDisplay(display.settings, m, { minRatio: display.minRatio }));
    }
    onLoad?.(e);
  };
  return (
    <div className={cn("relative overflow-hidden bg-secondary", className)} style={{ aspectRatio: String(ratio) }} data-fit={fit} data-testid={testId}>
      {state === "loading" && <div className="absolute inset-0 animate-pulse bg-secondary" aria-hidden="true" />}
      {whole && bands && state !== "error" && (
        <img src={imgUrl(src, 32)} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full scale-125 object-cover opacity-60 blur-2xl saturate-[1.2]" draggable={false} />
      )}
      {state === "error" ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground" data-testid={testId ? `${testId}-error` : undefined}>
          <ImageOff className="h-6 w-6" />
          <span className="text-[12px]">Biletet kunne ikkje lastast</span>
        </div>
      ) : (
        <Img
          src={src} alt={alt} sizes={sizes} width={width} widths={widths} priority={priority}
          onLoad={loaded} onError={() => setState("error")}
          style={whole ? undefined : { objectPosition: `${focus.x}% ${focus.y}%` }}
          className={cn("relative h-full w-full transition-[opacity,transform] duration-700 ease-out", whole ? "object-contain" : "object-cover", hover && !whole && "group-hover:scale-[1.025]", state === "loaded" ? "opacity-100" : "opacity-0", imgClassName)}
        />
      )}
      {children}
    </div>
  );
};
